import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment';

import CommonImage from '@/components/CommonImage';
import { useDownloadTechpackMutation } from '@/api/commonDownloadsApi';
import { toastError } from '@/components/Toast';
import { closeLoader, openLoader } from '@/store/actions/LoaderAction';
import { useGetDownloadCheckListQuery } from '@/features/design/api/designDetailsApi';
import useClickOutside from '@/hooks/useClickOutside';
import { useTechpackDesignUpdateStore } from '@/store/reducers';
import { setShowCheckList } from '@/features/design/redux/designUpdateSlice';
import { useToggleChecklistMutation } from '@/api/commonApis';
import { checkUserType } from '@/utils/Util';

const INITIAL_STATE = {
	all: true,
	materialList: true,
	measurementChart: true,
	printPDFtechpack: false,
	isPhysicalSampleAvailable: true,
	artboards: {}
};

const PrintAndDownload = ({ productId, onClose }) => {
	const { showChecklist } = useTechpackDesignUpdateStore();
	const [checkBoxValues, setCheckBoxValues] = useState(INITIAL_STATE);
	const [downloadTechpack] = useDownloadTechpackMutation();
	const dispatch = useDispatch();
	const { data: dropdownList } = useGetDownloadCheckListQuery({ id: productId });
	const printRef = useRef();
	useClickOutside(printRef, onClose);
	const [toggleChecklist] = useToggleChecklistMutation();
	const isMerchandiser = checkUserType('MERCHANDISER');

	// Initialize artboard selections when dropdownList arrives
	useEffect(() => {
		if (dropdownList?.artBoardNames) {
			setCheckBoxValues((prev) => {
				const artboardKeys = Object.keys(dropdownList.artBoardNames || {});
				const artboards = artboardKeys.reduce((acc, id) => {
					acc[id] = true;
					return acc;
				}, {});

				// compute `all` including artboards and physical sample
				const staticKeys = ['materialList', 'measurementChart', 'isPhysicalSampleAvailable'];
				const staticAll = staticKeys.every((k) => Boolean(prev[k]));
				const artboardsAll = artboardKeys.length ? Object.values(artboards).every(Boolean) : true;

				return {
					...prev,
					artboards,
					all: staticAll && artboardsAll
				};
			});
		}
		const shouldShowChecklist = isMerchandiser ? false : !dropdownList?.isChecklistHidden;
		dispatch(setShowCheckList(shouldShowChecklist));
	}, [dispatch, dropdownList?.isChecklistHidden, isMerchandiser]);

	const handleToggleCheckBox = (e) => {
		const { checked, name } = e.target;

		setCheckBoxValues((prev) => {
			const updated = {
				...prev,
				printPDFtechpack: false,
				[name]: checked
			};

			// compute `all` including dynamic artboards and physical sample
			const staticKeys = ['materialList', 'measurementChart', 'isPhysicalSampleAvailable'];
			const staticAll = staticKeys.every((k) => Boolean(updated[k]));
			const artboardsAll = Object.keys(updated.artboards || {}).length
				? Object.values(updated.artboards).every(Boolean)
				: true;

			updated.all = staticAll && artboardsAll;

			return updated;
		});
	};

	const handleToggleArtboard = (id, checked) => {
		setCheckBoxValues((prev) => {
			const artboards = {
				...prev.artboards,
				[id]: checked
			};

			const staticKeys = ['materialList', 'measurementChart', 'isPhysicalSampleAvailable'];
			const staticAll = staticKeys.every((k) => Boolean(prev[k]));
			const artboardsAll = Object.keys(artboards).length
				? Object.values(artboards).every(Boolean)
				: true;

			return {
				...prev,
				artboards,
				all: staticAll && artboardsAll,
				printPDFtechpack: false
			};
		});
	};

	const isCheckedAny = () => {
		// Check static flags first
		const staticVals = [
			checkBoxValues.materialList,
			checkBoxValues.isPhysicalSampleAvailable,
			checkBoxValues.measurementChart,
			checkBoxValues.printPDFtechpack
		];

		if (staticVals.some(Boolean)) return true;
		// then check artboards
		return Object.values(checkBoxValues.artboards || {}).some(Boolean);
	};

	const handleCheckAll = (e) => {
		const { checked } = e.target;
		if (checked) {
			setCheckBoxValues((prev) => {
				const artboards = Object.keys(prev.artboards || {}).reduce((acc, id) => {
					acc[id] = true;
					return acc;
				}, {});

				return {
					...prev,
					all: true,
					materialList: true,
					measurementChart: true,
					artboards,
					printPDFtechpack: false,
					isPhysicalSampleAvailable: true
				};
			});
		} else {
			setCheckBoxValues((prev) => {
				const artboards = Object.keys(prev.artboards || {}).reduce((acc, id) => {
					acc[id] = false;
					return acc;
				}, {});

				return {
					...prev,
					all: false,
					materialList: false,
					measurementChart: false,
					artboards,
					isPhysicalSampleAvailable: false
				};
			});
		}
	};

	const togglePrintPDFTechpack = () => {
		setCheckBoxValues((prev) => ({
			...prev,
			all: false,
			materialList: false,
			measurementChart: false,
			isPhysicalSampleAvailable: false,
			artboards: Object.keys(prev.artboards || {}).reduce((acc, id) => {
				acc[id] = false;
				return acc;
			}, {}),
			printPDFtechpack: !prev.printPDFtechpack
		}));
		dispatch(setShowCheckList(false));
	};

	const handleToggleChecklist = () => {
		dispatch(setShowCheckList(!showChecklist));

		toggleChecklist({
			params: {
				isHidden: showChecklist
			},
			productId
		})
			.unwrap()
			.then(() => {})
			.catch((error) => {
				toastError(error?.data?.message || 'Something went wrong');
			});
		setCheckBoxValues((prev) => {
			const updated = {
				...prev,
				printPDFtechpack: false
			};
			const staticKeys = dropdownList?.downloadPhysicalSample
				? ['materialList', 'measurementChart', 'isPhysicalSampleAvailable']
				: ['materialList', 'measurementChart'];
			const staticAll = staticKeys.every((k) => Boolean(updated[k]));
			const artboardsAll = Object.keys(updated.artboards || {}).length
				? Object.values(updated.artboards).every(Boolean)
				: true;
			updated.all = staticAll && artboardsAll;
			return updated;
		});
	};

	const getPayload = () => {
		let currentTime = moment(new Date()).format('DD MMM [AT] hh:mm A');
		const payload = {
			currentTime,
			artboardIds: [],
			hideChecklist: !showChecklist
		};

		if (dropdownList?.isMaterialListAvailable) {
			payload.downloadMaterialList = checkBoxValues?.materialList;
		}

		if (dropdownList?.isMeasurementChartAvailable) {
			payload.downloadMeasurementChart = checkBoxValues?.measurementChart;
		}
		if (dropdownList?.isPrintFileAvailable) {
			payload.printPdf = checkBoxValues?.printPDFtechpack;
		}

		if (dropdownList?.isPhysicalSampleAvailable) {
			payload.downloadPhysicalSample = checkBoxValues?.isPhysicalSampleAvailable;
		}

		// include selected artboard ids in `artboardIds` (only checked ones)
		const selectedArtboards = Object.entries(checkBoxValues.artboards || {})
			.filter(([, v]) => v)
			.map(([id]) => {
				const n = Number(id);
				return Number.isNaN(n) ? id : n;
			});

		// always set artboardIds to the selected list (empty array if none)
		payload.artboardIds = selectedArtboards;

		return payload;
	};

	const handlePrintCheckedItems = () => {
		dispatch(openLoader());
		downloadTechpack({ body: getPayload(), productId })
			.unwrap()
			.then(async (url) => {
				try {
					// Fetch the PDF ourselves to avoid the "attachment" behaviour
					const response = await fetch(url);
					if (!response.ok) {
						throw new Error('Failed to fetch PDF');
					}

					const blob = await response.blob();
					const blobUrl = window.URL.createObjectURL(new Blob([blob], { type: 'application/pdf' }));

					const printWindow = window.open(blobUrl, '_blank');
					if (printWindow) {
						printWindow.onload = () => {
							printWindow.print();
							setTimeout(() => {
								window.URL.revokeObjectURL(blobUrl);
							}, 60_000);
						};
					}
				} catch {
					toastError('Unable to open PDF for printing');
				} finally {
					dispatch(closeLoader());
				}
			})
			.catch((error) => {
				dispatch(closeLoader());
				toastError(error?.data?.message || 'Something went wrong!');
			});
	};

	const handleDownloadCheckedItems = () => {
		dispatch(openLoader());
		downloadTechpack({ body: getPayload(false), productId })
			.unwrap()
			.then((url) => {
				window.open(url, '_parent');
				dispatch(closeLoader());
			})
			.catch((error) => {
				dispatch(closeLoader());
				toastError(error?.data?.message || 'Something went wrong!');
			});
	};

	return (
		<div className="print-popup shadow-8dp position-absolute right-0 bg-white" ref={printRef}>
			<div className="print-header border-bottom px-3 mt-12 pb-2">
				<div className="custom-chekbox">
					<div className="form-group mb-0">
						<input
							id="all"
							name="all"
							type="checkbox"
							onChange={handleCheckAll}
							checked={checkBoxValues.all}
						/>
						<label className="regular-14 gray_dark_02" htmlFor="all">
							<span>All</span>
						</label>
					</div>
				</div>
			</div>

			<div className="print-popup-content px-3 pb-3 pt-2">
				<div className="print-popup-header pb-3">
					<p className="regular-14 gray_dark_02">ARTBOARD, TABLE & TP PDF</p>
				</div>
				<div className="print-popup-body">
					<div className="print-popup-items">
						<div className="custom-chekbox">
							<div className="form-group mb-2 border-bottom pb-2">
								{dropdownList?.isPhysicalSampleAvailable && (
									<div className="form-group mb-3">
										<input
											id="isPhysicalSampleAvailable"
											name="isPhysicalSampleAvailable"
											type="checkbox"
											checked={checkBoxValues.isPhysicalSampleAvailable}
											onChange={handleToggleCheckBox}
										/>
										<label
											className="regular-14 gray_dark_02"
											htmlFor="isPhysicalSampleAvailable"
										>
											<span>Sample Presentation</span>
										</label>
									</div>
								)}
								{dropdownList?.artBoardNames &&
									Object.keys(dropdownList.artBoardNames).length > 0 && (
										<div className="artboard-list">
											{Object.entries(dropdownList.artBoardNames).map(([id, name]) => (
												<div key={id} className="form-group mb-2">
													<input
														id={`artboard_${id}`}
														name={`artboard_${id}`}
														type="checkbox"
														checked={!!checkBoxValues.artboards?.[id]}
														onChange={(e) =>
															handleToggleArtboard(id, e.target.checked)
														}
													/>
													<label
														className="regular-14 gray_dark_02"
														htmlFor={`artboard_${id}`}
													>
														<span>{name || `Artboard ${id}`}</span>
													</label>
												</div>
											))}
										</div>
									)}
							</div>

							{dropdownList?.isMaterialListAvailable && (
								<div className="form-group mb-3">
									<input
										id="materialList"
										name="materialList"
										type="checkbox"
										checked={checkBoxValues.materialList}
										onChange={handleToggleCheckBox}
									/>
									<label className="regular-14 gray_dark_02" htmlFor="materialList">
										<span>Material list</span>
									</label>
								</div>
							)}
							{dropdownList?.isMeasurementChartAvailable && (
								<div
									className={`form-group mb-2 ${dropdownList?.isPrintFileAvailable ? 'border-bottom' : ''} pb-2`}
								>
									<input
										id="measurementChart"
										name="measurementChart"
										type="checkbox"
										checked={checkBoxValues.measurementChart}
										onChange={handleToggleCheckBox}
									/>
									<label className="regular-14 gray_dark_02" htmlFor="measurementChart">
										<span>Measurement chart</span>
									</label>
								</div>
							)}
							{dropdownList?.isPrintFileAvailable && (
								<div className="custom-radio-box custom-radio">
									<div className="form-group mb-2">
										<input
											id="printPDFtechpack"
											name="printPDFtechpack"
											type="radio"
											checked={checkBoxValues.printPDFtechpack}
											onClick={togglePrintPDFTechpack}
										/>
										<label className="regular-14 gray_dark_02" htmlFor="printPDFtechpack">
											<span>Print PDF techpack file</span>
										</label>
									</div>
								</div>
							)}
						</div>
					</div>
					<div className="print-popup-setting border-top pt-2 pb-4">
						<p className="regular-14 gray_dark_02 pb-2">SETTINGS</p>
						<div
							className="d-flex align-items-center cursor-pointer"
							onClick={() => handleToggleChecklist()}
						>
							{showChecklist ? (
								<CommonImage src="/icons/toggle-on.svg" />
							) : (
								<CommonImage src="/icons/toggle-off.svg" />
							)}
							<span className="regular-14 gray_dark_02 pl-2">
								{showChecklist ? 'Hide checklist' : 'Show checklist'}
							</span>
						</div>
					</div>
					<div className="print-popup-footer text-right">
						<button
							className={`button size32 outline`}
							disabled={!isCheckedAny()}
							onClick={handleDownloadCheckedItems}
						>
							Download
						</button>
						<button
							className="button size32 ml-2"
							disabled={!isCheckedAny()}
							onClick={handlePrintCheckedItems}
						>
							Print
						</button>
					</div>
				</div>
			</div>
		</div>
	);
};

export default PrintAndDownload;
